import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { updateDeliveryStatusWithNotification } from "../../utils/deliveryNotifications";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";
import OptimizedRouteMap from "../../components/OptimizedRouteMap";

// Utility function to extract lat,lng from any location string
function formatCoordinates(locationStr) {
  if (typeof locationStr !== 'string') return '';
  const matches = locationStr.match(/-?\d+\.\d+/g);
  if (matches && matches.length >= 2) {
    return `${matches[0]},${matches[1]}`;
  }
  return '';
}

const AssignedDeliveriesDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [selectedSlot, setSelectedSlot] = useState("Morning");
  const [partnerData, setPartnerData] = useState(null);

  useEffect(() => {
    const storedPartner = localStorage.getItem('deliveryPartner');
    if (storedPartner) {
      setPartnerData(JSON.parse(storedPartner));
    }
  }, []);

  useEffect(() => {
    const fetchDeliveries = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const q = query(
          collection(db, "deliveries"),
          where("date", "==", selectedDate),
          where("slot", "==", selectedSlot),
          where("assignedPartnerId", "==", user.uid)
        );

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDeliveries(data);
      } catch (err) {
        console.error("Error fetching deliveries:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveries();
  }, [user, selectedDate, selectedSlot]);

  const updateStatus = async (id, status) => {
    try {
      // Find the delivery to get customer info
      const delivery = deliveries.find(d => d.id === id);
      const customerName = delivery?.customerName || delivery?.userName || "Customer";
      const userId = delivery?.userId;
      
      // Use enhanced update function with notification
      const success = await updateDeliveryStatusWithNotification(
        id, 
        status, 
        customerName, 
        userId
      );
      
      if (success) {
        setDeliveries(prev => prev.map(d => d.id === id ? { ...d, status } : d));
      } else {
        console.error("Failed to update delivery status");
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  if (!user || !partnerData) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-green-700 mb-4">🚚 Delivery Partner Dashboard</h1>
          <p className="text-gray-600 mb-4">Please log in to access your dashboard</p>
          <button
            onClick={() => navigate('/delivery-partner/login')}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  const deliveryPoints = deliveries
    .filter(d => d.lat && d.lng && typeof d.lat === 'number' && typeof d.lng === 'number')
    .map(d => ({ 
      lat: Number(d.lat), 
      lng: Number(d.lng), 
      id: d.id, 
      name: d.customerName, 
      address: d.address 
    }));

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Delivery Partner Dashboard</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {partnerData.name || partnerData.email}</span>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-1 pb-4">
            <button
              onClick={() => navigate('/partner/dashboard')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                location.pathname === '/partner/dashboard'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📊 Overview
            </button>
            <button
              onClick={() => navigate('/partner/deliveries')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                location.pathname === '/partner/deliveries'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              📦 Assigned Deliveries
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-4 text-green-700">📦 Assigned Deliveries</h1>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <input
            type="date"
            value={selectedDate}
            onChange={e => {
              const formatted = dayjs(e.target.value).format("YYYY-MM-DD");
              setSelectedDate(formatted);
              console.log("Querying deliveries for date:", formatted);
            }}
            className="border px-3 py-2 rounded-lg"
          />
          <select
            value={selectedSlot}
            onChange={e => setSelectedSlot(e.target.value)}
            className="border px-3 py-2 rounded-lg"
          >
            <option value="Morning">Morning</option>
            <option value="Evening">Evening</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center text-green-600">Loading deliveries...</div>
        ) : deliveries.length === 0 ? (
          <div className="text-center text-gray-600">No deliveries found.</div>
        ) : (
          <>
            <div className="space-y-4">
              {deliveries.map(delivery => {
                let lat = delivery.lat;
                let lng = delivery.lng;
                let cleanedCoords = '';
                if ((typeof lat !== 'number' || typeof lng !== 'number') && typeof delivery.location === 'string') {
                  cleanedCoords = formatCoordinates(delivery.location);
                  if (cleanedCoords) {
                    [lat, lng] = cleanedCoords.split(',');
                  }
                } else if (typeof lat === 'number' && typeof lng === 'number') {
                  cleanedCoords = `${lat},${lng}`;
                }
                return (
                  <div key={delivery.id} className="bg-white p-4 rounded-xl shadow border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-lg font-semibold text-green-700">
                        Customer: {delivery.userName || delivery.customerName || delivery.userId}
                      </h2>
                      <p className="text-gray-700">📞 {delivery.phone || "Not available"}</p>
                        <p className="text-gray-600">
                          📍 {cleanedCoords ? cleanedCoords : (delivery.addressDetails || delivery.address || "Location not available")} — {delivery.area}
                        </p>
                      <p className="text-sm text-gray-500">Status: {delivery.status}</p>
                    </div>
                      {cleanedCoords ? (
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${cleanedCoords}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 underline mt-1 hover:text-blue-800"
                            >
                              📍 Start Navigation
                            </a>
                    ) : (
                        <span className="text-sm text-red-500 mt-1">⚠️ Location not available</span>
                    )}
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => updateStatus(delivery.id, "picked_up")}
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                    >
                      🚚 Picked Up
                    </button>
                    <button
                      onClick={() => updateStatus(delivery.id, "delivered")}
                      className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                    >
                      ✅ Delivered
                    </button>
                    <button
                      onClick={() => updateStatus(delivery.id, "not_delivered")}
                      className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      ❌ Not Delivered
                    </button>
                    <button
                      onClick={() => updateStatus(delivery.id, "delayed")}
                      className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                    >
                      🕒 Delayed
                    </button>
                    <a
                      href={`tel:${delivery.phone}`}
                      className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                    >
                      📞 Call
                    </a>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Optimized Route Map */}
            {deliveryPoints.length >= 2 && (
              <div className="mt-8 bg-white p-6 rounded-xl shadow-sm border border-green-100">
                <h2 className="text-lg font-semibold text-green-700 mb-2">🗺️ Optimized Route Map</h2>
                <OptimizedRouteMap deliveryPoints={deliveryPoints} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssignedDeliveriesDashboard; 